import { useEffect, useRef, useState } from "react";

/**
 * Premium animated counter — smoothly counts to `value` when scrolled into view.
 * Uses IntersectionObserver + requestAnimationFrame with an ease-out curve.
 */
export default function AnimatedCounter({ value = 0, duration = 1600, suffix = "+", className = "", testid }) {
  const [display, setDisplay] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || started) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) { setStarted(true); io.disconnect(); }
        });
      },
      { threshold: 0.35 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [started]);

  useEffect(() => {
    if (!started) return;
    let raf;
    const start = performance.now();
    const step = (t) => {
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(Math.round(eased * value));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [started, value, duration]);

  return (
    <span ref={ref} className={className} data-testid={testid}>
      {display}{suffix}
    </span>
  );
}
